import { useTheme } from '@/context/ThemeContext';

const solutions = [
  {
    tag: 'CAPTURE',
    title: 'Intake Capture Systems',
    subtitle: 'Never Miss an Inquiry',
    description:
      'Every inquiry is recorded and responded to instantly. Our intake systems eliminate the gap between a prospect reaching out and your business responding — ensuring zero revenue leakage at the first point of contact.',
    icon: 'ri-radar-line',
    image: 'https://public.readdy.ai/ai/img_res/9b5c5c8f2b146993435c787bfbb40ac3.jpg',
    features: ['Instant response automation', 'Multi-channel capture', 'Real-time qualification'],
  },
  {
    tag: 'CONVERT',
    title: 'Conversion Systems',
    subtitle: 'Guide Prospects to Action',
    description:
      'Intelligent systems that guide qualified prospects through a structured journey toward commitment. From initial interest to booked appointment or signed agreement — every step is deliberate and automated.',
    icon: 'ri-route-line',
    image: 'https://readdy.ai/api/search-image?query=abstract%20branching%20decision%20tree%20with%20glowing%20teal%20nodes%20and%20pathways%2C%20deep%20navy%20blue%20background%20with%20cyan%20connecting%20lines%2C%20minimalist%20data%20visualization%2C%20sophisticated%20tech%20diagram%20with%20cool%20ocean%20tones&width=600&height=400&seq=sol-convert-teal-001&orientation=landscape',
    features: ['Automated follow-up sequences', 'Intelligent lead scoring', 'Booking & scheduling integration'],
  },
  {
    tag: 'AUTOMATE',
    title: 'Workflow Automation',
    subtitle: 'Maintain Operational Flow',
    description:
      'Internal alignment across your entire operation. Our workflow infrastructure ensures information flows seamlessly between teams, tasks are executed on schedule, and nothing falls through the cracks.',
    icon: 'ri-settings-4-line',
    image: 'https://readdy.ai/api/search-image?query=abstract%20interconnected%20gear%20mechanisms%20with%20glowing%20teal%20highlights%20on%20deep%20navy%20background%2C%20minimalist%20industrial%20automation%20concept%2C%20sophisticated%20machinery%20visualization%20with%20cool%20blue%20teal%20aesthetic&width=600&height=400&seq=sol-automate-teal-001&orientation=landscape',
    features: ['Cross-team coordination', 'Task automation & routing', 'Performance monitoring'],
  },
];

export default function Solutions() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="solutions" className={`py-28 px-6 md:px-10 transition-colors duration-500 ${
      isDark ? 'bg-[#0d2847]' : 'bg-[#f8f9fb]'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className={`text-xs font-medium tracking-widest uppercase mb-4 block ${
              isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
            }`}>
              (CORE SYSTEMS)
            </span>
            <h2 className={`text-4xl md:text-6xl font-thin leading-tight ${
              isDark ? 'text-white/60' : 'text-[#0b1f3a]/60'
            }`}>
              Infrastructure That
            </h2>
            <h3 className={`text-4xl md:text-6xl font-bold leading-tight ${
              isDark ? 'text-white' : 'text-[#0b1f3a]'
            }`}>
              Eliminates Friction
            </h3>
          </div>
          <p className={`text-base leading-relaxed max-w-sm md:text-right ${
            isDark ? 'text-white/50' : 'text-slate-500'
          }`}>
            Three interconnected systems that work together to capture, convert, and sustain your business operations without manual intervention.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {solutions.map((sol) => (
            <div
              key={sol.tag}
              className={`group rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${
                isDark
                  ? 'bg-[#0b1f3a] border-white/10 hover:border-[#14b8c8]/40'
                  : 'bg-white border-slate-200 hover:border-[#0d7a8a]/40'
              }`}
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={sol.image}
                  alt={sol.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-7">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-widest mb-4 ${
                  isDark ? 'bg-[#14b8c8]/10 text-[#14b8c8]' : 'bg-[#0d7a8a]/10 text-[#0d7a8a]'
                }`}>
                  {sol.tag}
                </span>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-8 h-8 flex items-center justify-center ${
                    isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
                  }`}>
                    <i className={`${sol.icon} text-xl`}></i>
                  </div>
                  <h4 className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-[#0b1f3a]'}`}>{sol.title}</h4>
                </div>
                <p className={`text-sm font-medium mb-4 ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}>{sol.subtitle}</p>
                <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{sol.description}</p>

                <ul className="flex flex-col gap-2">
                  {sol.features.map((f) => (
                    <li key={f} className={`flex items-center gap-2 text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`ri-check-line text-xs ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}></i>
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Bottom border on hover */}
              <div className={`h-0.5 bg-gradient-to-r from-transparent via-${isDark ? '[#14b8c8]' : '[#0d7a8a]'} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}